import { Injectable } from '@angular/core'

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  constructor() {}

  /**
   * Downloads a file from a Base64-encoded string.
   *
   * @param {string} base64 The Base64 string representing the file content.
   * @param {string} filename The name of the file to be downloaded.
   * @param {string} mimeType The MIME type of the file (default: "application/octet-stream").
   */
  downloadBase64File(
    base64: string,
    filename: string,
    mimeType: string = 'application/octet-stream',
  ) {
    const link = document.createElement('a')
    link.href = `data:${mimeType};base64,${base64}`
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  /**
   * Handles the `keydown` event for keyboard navigation.
   * Allows navigating the list using arrow keys and selecting items with Enter.
   *
   * @param {KeyboardEvent} event - The keyboard event triggered when a key is pressed down.
   * @param {number} index - The current index of the focused list item.
   */
  onKeydown(event: KeyboardEvent, index: number): void {
    // Prevent default scroll behavior
    event.preventDefault()
    const listItems = document.querySelectorAll('a[mat-list-item]')

    if (event.key === 'ArrowDown') {
      // Focus the next item, if it exists
      if (index < listItems.length - 1)
        (listItems[index + 1] as HTMLElement).focus()
    } else if (event.key === 'ArrowUp') {
      // If the current index is greater than 0, focus on the previous item
      if (index > 0) (listItems[index - 1] as HTMLElement).focus()
    }
  }

  /**
   * Normalizes a date value to a Date object or null.
   *
   * @param {unknown} value - The value to normalize (can be Date, string, or other types).
   * @returns {Date | null} A valid Date object or null if the value cannot be converted to a valid date.
   */
  normalizeDate(value: unknown): Date | null {
    if (value instanceof Date) return value
    if (typeof value === 'string') {
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? null : d
    }
    return null
  }

  /**
   * Ensures a set of field-mapping aliases are unique (case-insensitive, trimmed).
   *
   * Callers should pass only the fields they want to validate, e.g.
   * `{ productIdentifier: form.productIdentifier, companyName: form.companyName }`.
   *
   * @param {Record<string, string>} fields Key/value map of aliases to validate.
   * @returns {boolean} True when all aliases are unique; false when duplicates exist.
   */
  ensureUniqueFieldAliases(fields: Record<string, string>): boolean {
    const seen = new Set<string>()

    for (const raw of Object.values(fields)) {
      if (raw === null || raw === undefined) continue

      const str = String(raw).trim()
      if (!str) continue

      const normalized = str.toLowerCase()
      if (seen.has(normalized)) return false
      seen.add(normalized)
    }

    return true
  }
}
